import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Users, Plus, Trash, PencilSimple, Heart, Phone, EnvelopeSimple, Cake, Drop, X, UserPlus
} from '@phosphor-icons/react';
import InviteFamilyModal from '@/components/InviteFamilyModal';
import { toast } from 'sonner';

const RELATIONS = ['Self', 'Spouse', 'Father', 'Mother', 'Son', 'Daughter', 'Brother', 'Sister', 'Grandfather', 'Grandmother', 'Other'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];
const REL_COLORS = { Self: '#FF6B35', Spouse: '#EC4899', Father: '#3B82F6', Mother: '#A855F7', Son: '#22C55E', Daughter: '#F59E0B', Brother: '#00E5FF', Sister: '#F472B6' };

export default function MembersPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', relation: 'Self', dob: '', blood_group: '', allergies: '', dietary_preferences: '', phone: '', email: '', emergency_contact: '', notes: '', monthly_allowance: 0, avatar: '' });
  const [showInvite, setShowInvite] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [kids, setKids] = useState([]);
  const [eaters, setEaters] = useState([]);

  const fetchPendingApprovals = async () => {
    try {
      const res = await api.get('/invites/pending-approvals');
      setPendingCount(res.data.count || 0);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { fetchPendingApprovals(); }, [api]); // eslint-disable-line

  const fetchMembers = async () => {
    try {
      const [res, k, e] = await Promise.all([api.get('/members'), api.get('/kids'), api.get('/meals/eater-profiles')]);
      setMembers(res.data); setKids(k.data); setEaters(e.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchMembers(); }, [api]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, monthly_allowance: Number(form.monthly_allowance) };
      if (editing) { await api.put(`/members/${editing}`, payload); } else { await api.post('/members', payload); }
      closeForm(); fetchMembers();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => { try { await api.delete(`/members/${id}`); fetchMembers(); } catch (e) { console.error(e); } };

  const openEdit = (m) => {
    setForm({ name: m.name, relation: m.relation || 'Self', dob: m.dob || '', blood_group: m.blood_group || '', allergies: m.allergies || '', dietary_preferences: m.dietary_preferences || '', phone: m.phone || '', email: m.email || '', emergency_contact: m.emergency_contact || '', notes: m.notes || '', monthly_allowance: m.monthly_allowance || 0, avatar: m.avatar || '' });
    setEditing(m.member_id); setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); setForm({ name: '', relation: 'Self', dob: '', blood_group: '', allergies: '', dietary_preferences: '', phone: '', email: '', emergency_contact: '', notes: '', monthly_allowance: 0, avatar: '' }); };

  const getAge = (dob) => { if (!dob) return ''; const d = new Date(dob); const now = new Date(); return Math.floor((now - d) / 31557600000) + ' yrs'; };

  const ageNum = (dob) => { if (!dob) return null; return Math.floor((Date.now() - new Date(dob)) / 31557600000); };
  const kidOf = (m) => kids.find(k => k.member_id === m.member_id || k.name.toLowerCase() === m.name.toLowerCase());
  const eaterOf = (m) => eaters.find(e => e.kind === 'individual' && (e.member_id === m.member_id || e.name.toLowerCase() === m.name.toLowerCase()));
  const isChild = (m) => ['Son', 'Daughter'].includes(m.relation) || (ageNum(m.dob) !== null && ageNum(m.dob) < 18);

  const createKidProfile = async (m) => {
    try {
      await api.post('/kids', { name: m.name, member_id: m.member_id, age: ageNum(m.dob) });
      toast.success(`Kid Zone profile created for ${m.name} — set their PIN in Kids Zone`);
      fetchMembers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const createEaterProfile = async (m) => {
    try {
      await api.post('/meals/eater-profiles', { name: m.name, kind: 'individual', member_id: m.member_id, age_years: ageNum(m.dob) || undefined, allergies: m.allergies ? m.allergies.split(',').map(s => s.trim()).filter(Boolean) : [] });
      toast.success(`Meal profile created for ${m.name} — tune preferences in Meals`);
      fetchMembers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="members-page" className="space-y-6 relative">
      <div className="glow-orb-teal -top-20 right-0 z-0" />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 relative z-10">
        <div>
          <p className="overline">Family</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">{t('family_members')}</h1>
          <p className="text-sm text-neutral-500 mt-1">{members.length} members</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setShowInvite(true)} data-testid="invite-family-btn"
            className="relative px-4 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-[#00E5FF]/30 text-[#00E5FF] text-sm font-semibold flex items-center gap-2 w-fit">
            <UserPlus size={16} weight="duotone" /> Invite via QR
            {pendingCount > 0 && (
              <span data-testid="pending-approval-badge"
                className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF6B35] text-white text-[10px] font-bold flex items-center justify-center"
                style={{ boxShadow: '0 0 12px rgba(255,107,53,0.6)' }}>
                {pendingCount}
              </span>
            )}
          </button>
          <button onClick={() => setShowForm(true)} data-testid="add-member-btn" className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow flex items-center gap-2 w-fit"><Plus size={16} /> {t('add_member')}</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
        {members.length === 0 ? (
          <div className="col-span-full text-center py-16"><Users size={48} weight="duotone" className="mx-auto text-neutral-600 mb-4" /><p className="text-neutral-500 text-sm">No family members added yet</p></div>
        ) : members.map(m => {
          const color = REL_COLORS[m.relation] || '#737373';
          return (
            <div key={m.member_id} className="glass-card p-6 animate-fade-up" data-testid={`member-card-${m.member_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center overflow-hidden" style={{ background: m.avatar ? 'transparent' : `${color}15` }}>
                    {m.avatar ? (
                      <img src={m.avatar} alt={m.name} className="w-full h-full object-cover rounded-2xl" />
                    ) : (
                      <span className="text-lg font-bold" style={{ color }}>{m.name[0]}</span>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{m.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: `${color}15`, color }}>{m.relation}</span>
                      {m.dob && <span className="text-[10px] text-neutral-500">{getAge(m.dob)}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(m)} className="p-1.5 rounded-lg hover:bg-white/5 text-neutral-500 hover:text-white"><PencilSimple size={14} /></button>
                  <button onClick={() => handleDelete(m.member_id)} className="p-1.5 rounded-lg hover:bg-white/5 text-neutral-500 hover:text-red-400"><Trash size={14} /></button>
                </div>
              </div>

              <div className="space-y-1.5 text-xs text-neutral-400">
                {m.blood_group && <div className="flex items-center gap-2"><Drop size={13} className="text-red-400" /> {m.blood_group}</div>}
                {m.phone && <div className="flex items-center gap-2"><Phone size={13} className="text-neutral-500" /> <a href={`tel:${m.phone}`} className="hover:text-[#FF6B35]">{m.phone}</a></div>}
                {m.email && <div className="flex items-center gap-2"><EnvelopeSimple size={13} className="text-neutral-500" /> {m.email}</div>}
                {m.dob && <div className="flex items-center gap-2"><Cake size={13} className="text-neutral-500" /> {m.dob}</div>}
                {m.allergies && <div className="flex items-center gap-2"><Heart size={13} className="text-amber-400" /> <span className="text-amber-400">{m.allergies}</span></div>}
              </div>

              {m.monthly_allowance > 0 && (
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                  <span className="text-[10px] text-neutral-500 uppercase">{t('allowance')}</span>
                  <span className="text-sm font-semibold text-white">Rs.{m.monthly_allowance.toLocaleString('en-IN')}/mo</span>
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-white/5 flex gap-1.5 flex-wrap" data-testid={`member-links-${m.member_id}`}>
                {kidOf(m) ? (
                  <a href="/kids" data-testid={`kid-badge-${m.member_id}`} className="text-[10px] font-semibold px-2 py-1 rounded-full bg-purple-500/10 text-purple-400 hover:bg-purple-500/20">🧒 Kid Zone ✓</a>
                ) : isChild(m) && (
                  <button onClick={() => createKidProfile(m)} data-testid={`create-kid-${m.member_id}`} className="text-[10px] font-semibold px-2 py-1 rounded-full border border-purple-500/30 text-purple-400 hover:bg-purple-500/10">+ Kid Zone</button>
                )}
                {eaterOf(m) ? (
                  <a href="/meals" data-testid={`eater-badge-${m.member_id}`} className="text-[10px] font-semibold px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20">🍲 Meal Profile ✓</a>
                ) : (
                  <button onClick={() => createEaterProfile(m)} data-testid={`create-eater-${m.member_id}`} className="text-[10px] font-semibold px-2 py-1 rounded-full border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">+ Meal Profile</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeForm}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">{editing ? 'Edit Member' : t('add_member')}</h3>
            <form onSubmit={handleSave} className="space-y-4">
              {/* Avatar Upload */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full border-2 border-dashed border-white/10 overflow-hidden bg-white/5 flex items-center justify-center flex-shrink-0">
                  {form.avatar ? (
                    <img src={form.avatar} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl text-neutral-600">{form.name ? form.name[0]?.toUpperCase() : '?'}</span>
                  )}
                </div>
                <div>
                  <label className="px-3 py-1.5 rounded-lg bg-white/5 text-xs text-neutral-400 cursor-pointer hover:bg-white/10 inline-block" data-testid="upload-avatar-btn">
                    {form.avatar ? 'Change Photo' : 'Upload Photo'}
                    <input type="file" accept="image/*" className="hidden" onChange={e => {
                      const file = e.target.files?.[0]; if (!file) return;
                      const reader = new FileReader(); reader.onload = () => setForm({...form, avatar: reader.result}); reader.readAsDataURL(file);
                    }} />
                  </label>
                  {form.avatar && (
                    <button type="button" onClick={() => setForm({...form, avatar: ''})} className="ml-2 text-xs text-red-400 hover:text-red-300">Remove</button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline block mb-2">{t('name')}</label><input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="member-name" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
                <div><label className="overline block mb-2">{t('relation')}</label><select value={form.relation} onChange={e => setForm({...form, relation: e.target.value})} data-testid="member-relation" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50">{RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline block mb-2">DOB</label><input type="date" value={form.dob} onChange={e => setForm({...form, dob: e.target.value})} data-testid="member-dob" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
                <div><label className="overline block mb-2">{t('blood_group')}</label><select value={form.blood_group} onChange={e => setForm({...form, blood_group: e.target.value})} data-testid="member-blood" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50"><option value="">--</option>{BLOOD_GROUPS.map(b => <option key={b} value={b}>{b}</option>)}</select></div>
              </div>
              <div><label className="overline block mb-2">{t('allergies')}</label><input type="text" value={form.allergies} onChange={e => setForm({...form, allergies: e.target.value})} data-testid="member-allergies" placeholder="e.g., Peanuts, Dust" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline block mb-2">{t('phone')}</label><input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="member-phone" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
                <div><label className="overline block mb-2">Email</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="member-email" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
              </div>
              <div><label className="overline block mb-2">{t('emergency_contact')}</label><input type="text" value={form.emergency_contact} onChange={e => setForm({...form, emergency_contact: e.target.value})} data-testid="member-emergency" placeholder="Name - Phone" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
              <div><label className="overline block mb-2">{t('allowance')} (Rs./month)</label><input type="number" value={form.monthly_allowance} onChange={e => setForm({...form, monthly_allowance: e.target.value})} data-testid="member-allowance" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" /></div>
              <div><label className="overline block mb-2">{t('notes')}</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} data-testid="member-notes" className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50 resize-none" /></div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeForm} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">{t('cancel')}</button>
                <button type="submit" data-testid="save-member-btn" className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">{t('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <InviteFamilyModal open={showInvite} onClose={() => setShowInvite(false)} onApproved={fetchPendingApprovals} />
    </div>
  );
}
